import { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import styled from 'styled-components';
import { useIntl } from 'react-intl';

import { FirstLeadGuidance } from 'app/features/leads/components/first-lead-guidance';
import { LeadsEmptyState } from 'app/features/leads/components/leads-empty-state';
import { LeadsFilteredEmpty } from 'app/features/leads/components/leads-filtered-empty';
import { LeadsTable } from 'app/features/leads/components/leads-table';
import { LeadsToolbar } from 'app/features/leads/components/leads-toolbar';
import { LeadsQueryProvider } from 'app/features/leads/providers/leads-query-provider';
import { leadsListQueryOptions } from 'app/features/leads/queries/leads-list-query-options';
import type { LeadStatusFilter, LeadSummary } from 'app/features/leads/services/gym-finder/types';
import { getLeadsPageState } from 'app/features/leads/utils/get-leads-page-state';
import ErrorComponent from 'app/ui/components/molecules/error';
import GenericPage from 'app/ui/components/layouts/generic-page';
import Loading from 'app/ui/components/molecules/loading';
import PageTitle from 'app/ui/components/molecules/page-title';
import Typography from 'app/ui/components/atoms/typography';
import { trackEvent } from 'app/utils/analytics/amplitude/tracker';

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: ${({ theme: { spacing } }) => `${spacing(8)} 0 ${spacing(6)}`};
`;

const LeadsPageContent = () => {
  const intl = useIntl();
  const pageTitle = intl.formatMessage({ id: 'leads.pageTitle' });

  const [status, setStatus] = useState<LeadStatusFilter>('all');
  const [search, setSearch] = useState('');

  // Search and status flow through the data-layer query options so React Query's
  // cache key drives refetching — no ad hoc local filtering on top of the result.
  const { data, isPending, isError } = useQuery(leadsListQueryOptions({ status, search }));

  useEffect(() => {
    trackEvent('leads_page_viewed', {});
  }, []);

  const handleViewDetails = useCallback((lead: LeadSummary) => {
    // Detail modal is implemented in a later slice; tracking the intent now keeps
    // analytics + e2e hooks stable when the modal lands.
    trackEvent('leads_view_details_clicked', { leadId: lead.id });
  }, []);

  if (isPending) {
    return <Loading />;
  }

  if (isError) {
    return <ErrorComponent />;
  }

  const pageState = getLeadsPageState({
    totalCount: data.totalCount,
    filteredCount: data.leads.length,
    profileComplete: data.profileComplete,
    search,
    status,
  });

  const isProfileIncomplete = pageState === 'zero-leads-profile-incomplete';

  const handleFirstLeadView = () => {
    const [lead] = data.leads;
    if (lead) {
      handleViewDetails(lead);
    }
  };

  return (
    <GenericPage pageTitle={pageTitle}>
      <HeaderRow>
        <PageTitle>
          <Typography variant="h3">{pageTitle}</Typography>
        </PageTitle>
      </HeaderRow>

      {isProfileIncomplete ? <LeadsEmptyState /> : null}

      {pageState === 'first-lead' ? (
        <>
          <FirstLeadGuidance onViewLead={handleFirstLeadView} />
          <LeadsToolbar search={search} status={status} onSearchChange={setSearch} onStatusChange={setStatus} />
          <LeadsTable leads={data.leads} onViewDetails={handleViewDetails} />
        </>
      ) : null}

      {pageState === 'filtered-empty' ? (
        <>
          <LeadsToolbar search={search} status={status} onSearchChange={setSearch} onStatusChange={setStatus} />
          <LeadsFilteredEmpty />
        </>
      ) : null}

      {!isProfileIncomplete && pageState === 'table' ? (
        <>
          <LeadsToolbar search={search} status={status} onSearchChange={setSearch} onStatusChange={setStatus} />
          <LeadsTable leads={data.leads} onViewDetails={handleViewDetails} />
        </>
      ) : null}
    </GenericPage>
  );
};

const LeadsPage = () => (
  <LeadsQueryProvider>
    <LeadsPageContent />
  </LeadsQueryProvider>
);

export default LeadsPage;
